"use client";

import { useCallback, useMemo, useState } from "react";
import { getSupabaseBrowserClient } from "@/lib/api/supabase";

export type AuthMode = "sign-in" | "sign-up";

export function useAuthForm(onSuccess: () => void) {
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);

  const [mode, setMode] = useState<AuthMode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = useCallback(() => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setError(null);
    setMessage(null);
    setLoading(false);
    setMode("sign-in");
  }, []);

  const switchMode = useCallback(() => {
    setMode((m) => (m === "sign-in" ? "sign-up" : "sign-in"));
    setError(null);
    setMessage(null);
  }, []);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setMessage(null);
      setLoading(true);

      try {
        if (mode === "sign-up") {
          if (password !== confirmPassword) {
            setError("Passwords do not match");
            setLoading(false);
            return;
          }

          const { data, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: `${window.location.origin}/api/auth/callback?next=${encodeURIComponent("/?welcome=1")}`,
            },
          });

          if (signUpError) {
            setError(signUpError.message);
            setLoading(false);
            return;
          }

          if (data.session) {
            onSuccess();
            window.location.reload();
          } else {
            setMessage("Check your email to confirm your account");
            setLoading(false);
          }
        } else {
          const { error: signInError } =
            await supabase.auth.signInWithPassword({ email, password });

          if (signInError) {
            setError(signInError.message);
            setLoading(false);
            return;
          }

          onSuccess();
          window.location.reload();
        }
      } catch {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      }
    },
    [mode, email, password, confirmPassword, supabase, onSuccess],
  );

  const handleGoogleAuth = useCallback(async () => {
    setError(null);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/api/auth/callback?next=${window.location.pathname}`,
      },
    });
  }, [supabase]);

  return {
    mode,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    error,
    message,
    loading,
    reset,
    switchMode,
    handleEmailSubmit,
    handleGoogleAuth,
  };
}
